"use client";
import React, { useState, useRef, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { ChevronDown, Globe } from 'lucide-react';
import { saveUserLanguagePreference } from '../../lib/geoip';

interface Language {
  code: string;
  name: string;
  flag: string;
  nativeName: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', flag: 'EN', nativeName: 'English' },
  { code: 'fr', name: 'French', flag: 'FR', nativeName: 'Français' }
];

export const LanguageSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('Navigation.languageSwitcher');

  // Get current locale from pathname as fallback
  const getCurrentLocaleFromPath = () => {
    if (!pathname) return 'en';
    const match = pathname.match(/^\/(en|ar|fr)/);
    return match ? match[1] : 'en';
  };

  // Use pathname-based locale detection for more reliable updates
  const currentLocale = getCurrentLocaleFromPath();
  const currentLanguage = languages.find(lang => lang.code === currentLocale) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (newLocale: string) => {
    if (newLocale === currentLocale) {
      setIsOpen(false);
      return;
    }

    setIsAnimating(true);
    
    // Dispatch custom event to trigger loading modal
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('language-switch', { 
        detail: { from: currentLocale, to: newLocale } 
      }));
    }
    
    // Save user language preference
    saveUserLanguagePreference(newLocale);
    
    // Add a small delay for smooth transition
    setTimeout(() => {
      // Get the current pathname
      let currentPath = pathname || '/';
      
      // Remove any existing locale prefix from the path
      const localePattern = /^\/(en|ar|fr)($|\/)/;
      const pathWithoutLocale = currentPath.replace(localePattern, '$2') || '/';
      
      // Create the new path with the new locale
      const newPath = `/${newLocale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`;
      
      console.log('Language switch:', { currentPath, pathWithoutLocale, newPath, currentLocale, newLocale });
      
      // Use router.push with refresh for better UX
      router.push(newPath);
      
      // Force refresh to ensure locale context updates
      setTimeout(() => {
        router.refresh();
      }, 100);
      
      setIsOpen(false);
      setIsAnimating(false);
    }, 150);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group relative flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-300 hover:scale-105 overflow-hidden"
        disabled={isAnimating}
      >
        
        <div className="relative z-10">
          <Globe className="w-4 h-4 text-white group-hover:text-blue-200 transition-colors duration-300" />
          {isOpen && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          )}
        </div>
        
        <div className="relative z-10 flex items-center gap-2">
          <span className="text-white font-semibold text-sm group-hover:text-blue-200 transition-colors duration-300">
            {isAnimating ? '...' : currentLanguage.flag}
          </span>
        </div>
        
        <ChevronDown 
          className={`relative z-10 w-4 h-4 text-white group-hover:text-blue-200 transition-all duration-300 ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`} 
        />
        
        {/* Enhanced Hover Effect Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"></div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-3 w-64 glass-morphism-dark rounded-lg shadow-2xl border border-white/20 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-300 ease-out">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-white">{t('selectLanguage')}</span>
            </div>
          </div>
          
          {/* Language Options */}
          <div>
            {languages.map((language, index) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center gap-4 px-4 py-4 text-left transition-all duration-300 group relative backdrop-blur-md ${
                  language.code === currentLocale 
                    ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/20 border-r-4 border-blue-400 shadow-sm' 
                    : 'glass-language-item hover:bg-gradient-to-r hover:from-white/15 hover:to-blue-500/10'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Language Code with enhanced glassmorphism styling */}
                <div className={`relative flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 backdrop-blur-sm ${
                  language.code === currentLocale 
                    ? 'bg-blue-400/40 shadow-lg scale-110 text-white border border-blue-300/60' 
                    : 'bg-white/15 group-hover:bg-blue-400/25 group-hover:scale-105 text-white group-hover:text-white border border-white/30 group-hover:border-blue-300/60'
                }`}>
                  <span className="flex items-center justify-center w-full h-full">{language.flag}</span>
                  {language.code === currentLocale && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full border-2 border-white/20 animate-pulse"></div>
                  )}
                </div>
                
                {/* Language Info */}
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm transition-colors duration-300 ${
                    language.code === currentLocale 
                      ? 'text-white' 
                      : 'text-white group-hover:text-white'
                  }`}>
                    {language.nativeName}
                  </div>
                   <div className={`text-xs transition-colors duration-300 ${
                     language.code === currentLocale 
                       ? 'text-white-80' 
                       : 'text-white-60 group-hover:text-blue-200'
                   }`}>
                    {language.name}
                  </div>
                </div>
                
                {/* Current Language Indicator */}
                {language.code === currentLocale && (
                  <div className="flex items-center gap-2">
                    <div className="px-2 py-1 bg-blue-500/50 text-white text-xs font-medium rounded-full shadow-sm backdrop-blur-sm border border-blue-300/30">
                      {t('active')}
                    </div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                
                {/* Enhanced Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                
                {/* Subtle glow for inactive items */}
                {language.code !== currentLocale && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                )}
              </button>
            ))}
          </div>
          
          {/* Enhanced Footer */}
          <div className="px-4 py-3 bg-gradient-to-r from-gray-900/60 to-blue-900/40 border-t border-white/10 backdrop-blur-md">
            <div className="flex items-center justify-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
              <p className="text-xs text-white font-medium">
                {t('choosePreferred')}
              </p>
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
